import type React from 'react';
import {useState} from 'react';
import {Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography} from '@mui/material';
import {errorText} from '../../hooks/notices';
import type {OutcomeInput, PriceModel} from '../../types/shouldCostTypes';
import {LOCALE} from '~/pages/category-profile/domain/locale';

type Props = {model: PriceModel; onSubmit: (input: OutcomeInput) => Promise<unknown>; onClose: () => void};

/** Record a closed negotiation: the agreed price, optionally per line. The model's should cost today is its prediction. */
export const OutcomeDialog: React.FC<Props> = ({model, onSubmit, onClose}) => {
    const [supplier, setSupplier] = useState(model.quotes[0]?.supplier ?? '');
    const [closedOn, setClosedOn] = useState(new Date().toISOString().slice(0, 10));
    const [unitPrice, setUnitPrice] = useState('');
    const [lines, setLines] = useState<Record<string, string>>({});
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const ready = supplier.trim() && closedOn && Number(unitPrice) > 0;
    const submit = () => {
        setBusy(true);
        setError(null);
        const breakdown = Object.fromEntries(Object.entries(lines).filter(([, v]) => v.trim() !== '').map(([k, v]) => [k, Number(v)]));
        onSubmit({supplier: supplier.trim(), closedOn, unitPrice: Number(unitPrice), lines: breakdown, note})
            .then(onClose).catch(e => setError(errorText(e))).finally(() => setBusy(false));
    };
    return (
        <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="cp-outcome-title">
            <DialogTitle id="cp-outcome-title">Record negotiation outcome</DialogTitle>
            <DialogContent>
                <Stack gap={2} sx={{pt: 1}}>
                    {error && <Alert severity="error">{error}</Alert>}
                    <Stack direction={{xs: 'column', sm: 'row'}} gap={2}>
                        <TextField label="Supplier" size="small" value={supplier} sx={{flex: 2}} onChange={e => setSupplier(e.target.value)}/>
                        <TextField label="Closed on" size="small" type="date" value={closedOn} sx={{flex: 1}}
                                   onChange={e => setClosedOn(e.target.value)} slotProps={{inputLabel: {shrink: true}}}/>
                    </Stack>
                    <TextField label={`Agreed price per ${model.unit} (${model.currency})`} size="small" type="number" value={unitPrice}
                               onChange={e => setUnitPrice(e.target.value)}/>
                    <Typography variant="subtitle2">Agreed price by line (optional)</Typography>
                    {model.lines.map(l => (
                        <TextField key={l.id} label={l.name} size="small" type="number" value={lines[String(l.id)] ?? ''}
                                   onChange={e => setLines({...lines, [String(l.id)]: e.target.value})}
                                   helperText={`Predicted ${l.cost.toLocaleString(LOCALE, {maximumFractionDigits: 4})}`}/>
                    ))}
                    <TextField label="Note" size="small" multiline minRows={2} value={note} onChange={e => setNote(e.target.value)}/>
                </Stack>
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2}}>
                <Button onClick={onClose} disabled={busy}>Cancel</Button>
                <Button variant="contained" onClick={submit} disabled={busy || !ready}>Record outcome</Button>
            </DialogActions>
        </Dialog>
    );
};
